import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Form, Card, Alert } from 'react-bootstrap';
import { useGetCustomer } from '../../../services/customerService';
// Direct loan account creation via v1 API is deprecated.
import { DirectLoanAccountRequest, LoanDetails } from '../../../services/schemas/loanServiceSchemas';
import { Customer } from '../../../services/schemas/customerServiceSchemas';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import LoanDetailsStep from '../../loan-application/steps/LoanDetailsStep';
import { toast } from 'react-toastify';
import { maxLoanAmountFor } from '../../../constants/loanLimits';
import { toLocalDateString } from '../../../utils/localDate';

// Form schema for the create account form
const createAccountSchema = z.object({
  customerId: z.string().min(1, 'Customer selection is required'),
  customerSearchTerm: z.string().optional(),
  loan: z.object({
    calculationMode: z.enum(['AUTOMATIC', 'CUSTOM']),
    loanProductType: z.enum(['STANDARD', 'TIRHELO', 'SEDFA']).optional(),
    customLoanType: z.enum(['ONCE_OFF', 'INSTALLMENTS']).optional(),
    loanAmount: z.number().min(1, 'Loan amount must be greater than 0'),
    loanTerm: z.number().min(1, 'Loan term must be greater than 0'),
    loanPurpose: z.string().min(1, 'Loan purpose is required'),
    securityType: z.enum(['SECURED', 'UNSECURED']),
    interestRate: z.number().min(0).max(100).optional(),
    adminFee: z.number().min(0).optional(),
    totalRepayment: z.number().min(0).optional(),
    interestAmount: z.number().min(0).optional(),
    dueDate: z.string().optional(),
    commencementDate: z.string().optional(),
    numberOfInstallments: z.number().min(1).max(12).optional()
  }).superRefine((loan, ctx) => {
    const loanProductType =
      loan.loanProductType || (loan.calculationMode === 'AUTOMATIC' ? 'STANDARD' : 'TIRHELO');
    const isTirhelo = loanProductType === 'TIRHELO';
    const maxLoanAmount = maxLoanAmountFor(loanProductType === 'SEDFA');

    if (!isTirhelo) {
      if (loan.loanAmount < 3000) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Loan amount must be at least R3,000',
          path: ['loanAmount']
        });
      }

      if (loan.loanAmount > maxLoanAmount) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            loanProductType === 'SEDFA'
              ? 'SEDFA Loan amount cannot exceed R250,000'
              : 'Loan amount cannot exceed R1,500,000',
          path: ['loanAmount']
        });
      }
    }
  })
});

type CreateAccountFormValues = z.infer<typeof createAccountSchema>;

const CreateDirectLoanAccount: React.FC = () => {
    const navigate = useNavigate();
    const [customerSearchTerm, setCustomerSearchTerm] = useState('');
    const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
    const [isSearchingCustomer, setIsSearchingCustomer] = useState(false);

    // Initialize form for create account
    const createAccountMethods = useForm<CreateAccountFormValues>({
        resolver: zodResolver(createAccountSchema),
        defaultValues: {
            customerId: '',
            customerSearchTerm: '',
            loan: {
                calculationMode: 'CUSTOM',
                loanProductType: 'TIRHELO',
                customLoanType: 'ONCE_OFF',
                loanAmount: 0,
                loanTerm: 12,
                loanPurpose: '',
                securityType: 'UNSECURED',
                interestRate: 0,
                adminFee: 0,
                totalRepayment: 0,
                interestAmount: 0,
                dueDate: '',
                commencementDate: '',
                numberOfInstallments: 1
            }
        }
    });

    // Customer search query - only enabled when searching
    const { data: customerSearchResults, isLoading: isLoadingCustomers } = useGetCustomer(
        customerSearchTerm && isSearchingCustomer 
            ? { registrationNumber: customerSearchTerm.includes('/') ? customerSearchTerm : undefined, idNumber: !customerSearchTerm.includes('/') ? customerSearchTerm : undefined }
            : undefined,
        {
            query: {
                enabled: isSearchingCustomer && !!customerSearchTerm
            }
        }
    );

    // Deprecated: direct account creation disabled in ledger redesign. Show message on submit.

    // Set default dates when component mounts
    useEffect(() => {
        // Set default commencement date to today
        const today = new Date();
        const formattedToday = toLocalDateString(today);
        createAccountMethods.setValue('loan.commencementDate', formattedToday);
        
        // Set default due date to last day of current month
        const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        const formattedLastDay = toLocalDateString(lastDayOfMonth);
        createAccountMethods.setValue('loan.dueDate', formattedLastDay);
    }, [createAccountMethods]);

    const handleCustomerSearch = () => {
        if (!customerSearchTerm.trim()) {
            toast.error('Please enter a customer ID number or registration number');
            return;
        }
        setIsSearchingCustomer(true);
    };

    // Handle customer search results
    useEffect(() => {
        if (customerSearchResults && isSearchingCustomer) {
            setSelectedCustomer(customerSearchResults);
            createAccountMethods.setValue('customerId', customerSearchResults.id || '');
            setIsSearchingCustomer(false);
            toast.success('Customer found!');
        } else if (isSearchingCustomer && customerSearchResults === null) {
            setIsSearchingCustomer(false);
            toast.error('Customer not found');
        }
    }, [customerSearchResults, isSearchingCustomer, createAccountMethods]);

    const handleSubmitCreateAccount = async (data: CreateAccountFormValues) => {
        if (!selectedCustomer) {
            toast.error('Please select a customer first');
            return;
        }

        // Prepare the loan details payload
        const loanDetails: LoanDetails = {
            loanAmount: data.loan.loanAmount,
            loanTerms: data.loan.loanTerm,
            loanPurpose: data.loan.loanPurpose,
            securityType: data.loan.securityType,
            calculationMode: data.loan.calculationMode,
            customLoanType: data.loan.customLoanType,
            commencementDate: data.loan.commencementDate,
            dueDate: data.loan.dueDate,
            numberOfInstallments: data.loan.numberOfInstallments,
            interestRate: data.loan.interestRate,
            adminFee: data.loan.adminFee,
            totalRepayment: data.loan.totalRepayment,
            interestAmount: data.loan.interestAmount
        };

        const payload: DirectLoanAccountRequest = {
            customerId: data.customerId,
            loanDetails: loanDetails
        };

        toast.info('Direct account creation is no longer supported. Please use the application workflow.');
    };

    const handleCancel = () => {
        navigate('/accounts');
    };

    return (
        <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
            <div className="container-fluid">
                {/* Breadcrumb */}
                <div className="row">
                    <div className="col mb-3">
                        <div className="page-breadcrumb d-flex align-items-center">
                            <div className="pe-3">
                                <div className="px-2 rounded-circle bg-primary-light">
                                    <i className="fa fa-plus text-primary"></i>
                                </div>
                            </div>
                            <div className="flex-fill">
                                <ol className="breadcrumb mb-0">
                                    <li className="breadcrumb-item"><Link to="/">Home</Link></li>
                                    <li className="breadcrumb-item"><Link to="/accounts">Loan Accounts</Link></li>
                                    <li className="breadcrumb-item active" aria-current="page">Create Direct Account</li>
                                </ol>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Header */}
                <div className="row g-3 mb-3">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-header d-flex justify-content-between align-items-center">
                                <h6 className="card-title mb-0">Create Direct Loan Account</h6>
                                <div className="d-flex gap-2">
                                    <Button variant="secondary" onClick={handleCancel}>
                                        <i className="fa fa-arrow-left me-2"></i>
                                        Back to Accounts
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <FormProvider {...createAccountMethods}>
                    <form onSubmit={createAccountMethods.handleSubmit(handleSubmitCreateAccount)}>
                        {/* Customer Search Section */}
                        <div className="row g-3 mb-3">
                            <div className="col-12">
                                <div className="card">
                                    <div className="card-header">
                                        <h6 className="card-title mb-0">Customer Selection</h6>
                                    </div>
                                    <div className="card-body">
                                        <div className="row g-3">
                                            <div className="col-md-8">
                                                <Form.Group>
                                                    <Form.Label>Customer ID Number / Registration Number</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        placeholder="Enter ID number or registration number"
                                                        value={customerSearchTerm}
                                                        onChange={(e) => setCustomerSearchTerm(e.target.value)}
                                                    />
                                                </Form.Group>
                                            </div>
                                            <div className="col-md-4 d-flex align-items-end">
                                                <Button
                                                    variant="primary"
                                                    onClick={handleCustomerSearch}
                                                    disabled={isLoadingCustomers}
                                                    className="w-100"
                                                >
                                                    {isLoadingCustomers ? (
                                                        <>
                                                            <span className="spinner-border spinner-border-sm me-2" />
                                                            Searching...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="fa fa-search me-2"></i>
                                                            Search Customer
                                                        </>
                                                    )}
                                                </Button>
                                            </div>
                                        </div>

                                        {selectedCustomer && (
                                            <Alert variant="success" className="mt-3">
                                                <div className="d-flex align-items-center">
                                                    <i className="fa fa-check-circle me-2"></i>
                                                    <div>
                                                        <strong>Customer Selected:</strong><br />
                                                        {selectedCustomer.info?.type === 'INDIVIDUAL' 
                                                            ? `${(selectedCustomer.info as any)?.firstName} ${(selectedCustomer.info as any)?.lastName}`
                                                            : (selectedCustomer.info as any)?.registeredName || 'Company'
                                                        }
                                                        <br />
                                                        <small className="text-muted">
                                                            ID: {selectedCustomer.info?.type === 'INDIVIDUAL' 
                                                                ? (selectedCustomer.info as any)?.idNumber
                                                                : (selectedCustomer.info as any)?.registrationNumber
                                                            }
                                                        </small>
                                                    </div>
                                                </div>
                                            </Alert>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Loan Details Section */}
                        {selectedCustomer && (
                            <div className="row g-3 mb-3">
                                <div className="col-12">
                                    <div className="card">
                                        <div className="card-header">
                                            <h6 className="card-title mb-0">Loan Details</h6>
                                        </div>
                                        <div className="card-body">
                                            <LoanDetailsStep />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Action Buttons */}
                        {selectedCustomer && (
                            <div className="row g-3">
                                <div className="col-12">
                                    <div className="card">
                                        <div className="card-body">
                                            <div className="d-flex justify-content-end gap-2">
                                                <Button variant="secondary" onClick={handleCancel}>
                                                    Cancel
                                                </Button>
                                                <Button variant="primary" type="submit">
                                                    <i className="fa fa-plus me-2"></i>
                                                    Create Loan Account
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </form>
                </FormProvider>
            </div>
        </div>
    );
};

export default CreateDirectLoanAccount; 
